<template>
  <div>
    <RightSidePanel title="Email" description="Email a candidate or teammate" @handleClosePanel="handleClosePanel">
      <div class="mt-4 bg-gray-50 px-4 py-6 sm:px-6">
        <TextInput
          ref="subjectInput"
          label="Subject"
          placeholder="Greetings from earth!"
          :modelValue="email.subject"
          @update:modelValue="
            value => {
              email.subject = value;
            }
          "
        />
        <div class="mt-8 flex space-x-3">
          <div class="min-w-0 flex-1">
            <QuillInput
              ref="quillInput"
              name="new-email"
              label="New Email"
              :labelSrOnly="true"
              size="large"
              :modelValue="email.body"
              @update:modelValue="value => (email.body = value)"
            />

            <div class="mt-4 flex items-center justify-between">
              <FileInputBasic :multiple="true" :modelValue="files" @update:modelValue="files = $event" accept="*" />
              <TButton label="Send" color="indigo" @click="sendEmail">
                <LocalLoadingSpinner />
              </TButton>
            </div>

            <div class="mt-8 grid grid-cols-2 gap-3">
              <SelectMenu
                label="Merge Tags"
                :initialValue="selectMenuInitialValue"
                :options="mergeTags"
                @selectedOption="insertMergeTag($event)"
              />
              <SelectMenu
                label="Use existing template"
                :initialValue="selectedTemplateName"
                :options="emailTemplates.map(t => t.name)"
                @selectedOption="template => setEmailTemplate(template)"
                ref="emailTemplateSelectMenu"
              />
            </div>

            <div class="mt-4 bg-gray-100 rounded p-6 text-sm flex justify-between">
              <div class="mt-2 text-sm leading-5 text-gray-900">Looking to create more email templates?</div>
              <router-link to="/settings/templates/email" target="_blank">
                <TButton label="Template Library" color="white" />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </RightSidePanel>
  </div>
</template>

<script>
import RightSidePanel from '../shared/RightSidePanel.vue';
import FileInputBasic from '../shared/FileInputBasic.vue';
import { sanitizeRichText } from '../../utilities/sanitization';
import QuillInput from './QuillInput.vue';

export default {
  props: ['record'],
  components: {
    RightSidePanel,
    FileInputBasic,
    QuillInput,
  },
  data() {
    return {
      email: {
        subject: '',
        body: '',
      },
      emailTemplates: [],
      selectedTemplateId: '',
      selectedTemplate: null,
      name: '',
      loading: false,
      selectMenuInitialValue: 'Insert a merge tag...',
      mergeTags: [
        '{{contact.name}}',
        '{{contact.first_name}}',
        '{{contact.last_name}}',
        '{{contact.email}}',
        '{{contact.phone_number}}',
        '{{contact.source}}',
        '{{contact.referral_link}}',
        '{{team.company}}',
      ],
      files: null,
    };
  },
  computed: {
    selectedTemplateName() {
      const template = this.emailTemplates.find(t => t.id === this.selectedTemplateId);
      return template ? template.name : 'Select an email template...';
    }
  },
  mounted() {
    this.getEmailTemplates();
  },
  methods: {
    sanitize(html) {
      return sanitizeRichText(html);
    },
    insertMergeTag(tag) {
      if (tag === this.selectMenuInitialValue) return;
      this.$refs.quillInput.insertTextAtCursor(tag);
    },
    sendEmail() {
      const self = this;
      let formData = new FormData();
      formData.append('email', self.record.email);
      formData.append('subject', self.email.subject);
      formData.append('body', self.email.body);
      formData.append('contact_id', self.record.id);
      if (self.files) {
        for (let i = 0; i < self.files.length; i++) {
          formData.append('email_files[]', self.files[i], self.files[i].name);
        }
      }
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/contacts/${self.record.id}/send_email`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          if (data.status != 'error') {
            self.files = null;
            self.$emit('reload');
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Email sent',
              alertDescription:
                'Your email was successfully sent to this contact.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Email sending error',
              alertDescription: data.message,
            });
          }
        });
    },
    getEmailTemplates() {
      const self = this;
      fetch(`/api/email_templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => response.json())
        .then(data => {
          self.emailTemplates = data.records;
        });
    },
    setEmailTemplate(templateName) {
      if (this.emailTemplates.length === 0) return;
      const self = this;
      const selectedTemplate = self.emailTemplates.find(t => t.name === templateName);
      if (!selectedTemplate) return;
      
      self.selectedTemplate = selectedTemplate;
      self.name = selectedTemplate.name;
      
      this.$nextTick(() => {
        self.email.subject = selectedTemplate.subject;
        self.email.body = selectedTemplate.body;
        self.selectedTemplateId = selectedTemplate.id;
        if (self.$refs.subjectInput) {
          self.$refs.subjectInput.textValue = selectedTemplate.subject;
        }
      });
    },
    handleClosePanel() {
      this.$emit('handleContactEmailCancel');
    },
  },
};
</script>
